import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";

import { SURVEY_RESPONSES_SUMMARY_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";

// order + display titles for the 5 questions; the answers themselves (and their
// counts) come from the backend, sourced straight from the stored responses.
const QUESTION_TITLES = {
  current_behaviour: "Q1. What best describes your current trading behaviour?",
  reduce_reason: "Q2. What was the primary reason or trigger that led you to reduce or stop trading?",
  specific_incident: "Q3. Was there a specific experience or incident that influenced your decision?",
  resume_change: "Q4. What ONE change would be most likely to make you resume trading?",
  satisfaction: "Q5. How satisfied were you with your overall experience before you reduced/stopped trading?",
};

export default function SummaryTab() {
  const { getValidToken } = useAuth();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const response = await fetch(SURVEY_RESPONSES_SUMMARY_ENDPOINT, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch survey summary");
        setSummary(await response.json());
      } catch (e) {
        setError(e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return <Typography sx={{ ...typography.bodyLarge, p: 3 }}>{error}</Typography>;
  }
  if (!summary) {
    return <Typography sx={{ ...typography.bodyLarge, p: 3 }}>Loading…</Typography>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {Object.entries(QUESTION_TITLES).map(([field, title]) => {
        const options = summary.questions?.[field] || [];
        // Every COMPLETED response answers all 5 questions together, so
        // `answered_total` is the same population for every question here —
        // dividing each option's count by it means a question's percentages
        // always sum to 100%, and the bar width matches the percentage shown.
        const answeredTotal = summary.answered_total || 0;
        // Decimal (not rounded-integer) percentage: equal counts must always
        // show equal percentages, which integer rounding can't guarantee once
        // you try to force everything to sum to exactly 100.
        const percentOf = (count) => (answeredTotal ? (100 * count) / answeredTotal : 0);

        return (
          <Box
            key={field}
            sx={{ border: "1px solid #DCDFE5", borderRadius: "12px", p: 2.5, bgcolor: "#fff" }}
          >
            <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: "#1D2330", mb: 1.5 }}>
              {title}
            </Typography>
            {options.length === 0 && (
              <Typography sx={{ ...typography.bodySmall, color: "#6C7C93" }}>No responses yet.</Typography>
            )}
            {options.map((option) => (
              <Box key={option.code} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <Typography sx={{ ...typography.bodySmall, color: "#1D2330", flex: "0 0 260px" }}>
                  {option.label}
                </Typography>
                <Box sx={{ flex: 1, height: 10, bgcolor: "#F0F5FF", borderRadius: "6px", overflow: "hidden" }}>
                  <Box
                    sx={{
                      height: "100%",
                      borderRadius: "6px",
                      bgcolor: ColorsConstants.primaryPurple100,
                      width: `${percentOf(option.count)}%`,
                    }}
                  />
                </Box>
                <Typography sx={{ ...typography.bodySmall, color: "#6C7C93", flex: "0 0 90px", textAlign: "right" }}>
                  {option.count} ({percentOf(option.count).toFixed(1)}%)
                </Typography>
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}
